import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Sprout,
  Leaf,
  Trees,
  Wind,
  Snowflake,
  Hammer,
  Layers,
  CheckCircle,
  Award,
  Users,
  MessageSquare,
  Globe,
  Heart,
  Star,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  Check,
  Menu,
  X,
  Upload,
  Sparkles
} from 'lucide-react';
import { services, companyValues, testimonials, beforeAfterImages, faqs } from '../mockData';

const iconMap = {
  Sprout,
  Leaf,
  Trees,
  Wind,
  Snowflake,
  Hammer,
  Layers,
  Sparkles,
  CheckCircle,
  Award,
  Users,
  MessageSquare,
  Globe,
  Heart
};

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    timeframe: '',
    message: ''
  });
  const [photos, setPhotos] = useState([]);

  const [submitStatus, setSubmitStatus] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const preset = new URLSearchParams(window.location.search).get('service');
    if (preset) {
      setFormData(prev => ({ ...prev, service: preset }));
      setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    setPhotos(prev => [...prev, ...files]);
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('Sending...');
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Use FormData to send files
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('service', formData.service);
      formDataToSend.append('timeframe', formData.timeframe);
      formDataToSend.append('message', formData.message);
      
      // Append each photo
      photos.forEach((photo) => {
        formDataToSend.append('photos', photo);
      });
      
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        body: formDataToSend,
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSubmitStatus(data.message || 'Thank you! Our team will review your request and contact you regarding next steps.');
        setFormData({ name: '', email: '', phone: '', address: '', service: '', timeframe: '', message: '' });
        setPhotos([]);
        setTimeout(() => setSubmitStatus(''), 5000);
      } else {
        setSubmitStatus('Something went wrong. Please try again or call us directly.');
        setTimeout(() => setSubmitStatus(''), 5000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('Something went wrong. Please try again or call us directly.');
      setTimeout(() => setSubmitStatus(''), 5000);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      {/* Premium Header with 3D Logo */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-slate-950/98 via-emerald-950/95 to-slate-950/98 backdrop-blur-xl border-b border-emerald-500/20 shadow-[0_4px_30px_rgba(16,185,129,0.15)]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20 md:h-28">
            {/* Logo - Bigger, Left Corner, Moved Down */}
            <Link to="/" className="flex items-center group -ml-2 md:-ml-4 mt-1 md:mt-2">
              <div className="relative">
                {/* Glow effect behind logo */}
                <div className="absolute -inset-2 md:-inset-3 bg-gradient-to-r from-emerald-500/40 via-green-400/30 to-emerald-500/40 rounded-2xl blur-xl opacity-70 group-hover:opacity-100 transition-opacity duration-500 animate-pulse-slow"></div>
                {/* Logo with 3D shadow - BIGGER */}
                <div className="relative transform group-hover:scale-105 transition-transform duration-300">
                  <img 
                    src="https://customer-assets-lqy194kg.emergentagent.net/job_90e246be-0711-4e27-8caa-13f1ea345ba5/artifacts/pl6ilas6_TNA%20Logo.png" 
                    alt="True North Alberta Property Services" 
                    className="h-16 md:h-24 w-auto rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_60px_rgba(16,185,129,0.3)] border-2 border-emerald-400/30"
                    style={{filter: 'drop-shadow(0 0 20px rgba(16,185,129,0.4))'}}
                  />
                </div>
              </div>
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/services" className="relative text-gray-200 hover:text-emerald-400 transition-all duration-300 font-semibold text-sm uppercase tracking-wider group">
                Services
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-green-400 group-hover:w-full transition-all duration-300"></span>
              </Link>
              <Link to="/commercial" className="relative text-gray-200 hover:text-emerald-400 transition-all duration-300 font-semibold text-sm uppercase tracking-wider group">
                Commercial
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-green-400 group-hover:w-full transition-all duration-300"></span>
              </Link>
              <Link to="/gallery" className="relative text-gray-200 hover:text-emerald-400 transition-all duration-300 font-semibold text-sm uppercase tracking-wider group">
                Gallery
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-green-400 group-hover:w-full transition-all duration-300"></span>
              </Link>
              <Link to="/values" className="relative text-gray-200 hover:text-emerald-400 transition-all duration-300 font-semibold text-sm uppercase tracking-wider group">
                Our Values
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-green-400 group-hover:w-full transition-all duration-300"></span>
              </Link>
              <Link to="/testimonials" className="relative text-gray-200 hover:text-emerald-400 transition-all duration-300 font-semibold text-sm uppercase tracking-wider group">
                Testimonials
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-green-400 group-hover:w-full transition-all duration-300"></span>
              </Link>
              <Link to="/faq" className="relative text-gray-200 hover:text-emerald-400 transition-all duration-300 font-semibold text-sm uppercase tracking-wider group">
                FAQ
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-green-400 group-hover:w-full transition-all duration-300"></span>
              </Link>
              <Button 
                onClick={scrollToContact} 
                className="relative overflow-hidden bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-600 hover:from-emerald-500 hover:via-green-400 hover:to-emerald-500 text-white font-bold px-8 py-3 rounded-full shadow-[0_4px_20px_rgba(16,185,129,0.4)] hover:shadow-[0_6px_30px_rgba(16,185,129,0.6)] transition-all duration-300 border border-emerald-400/30"
              >
                <span className="relative z-10">Get a Quote</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-200%] hover:translate-x-[200%] transition-transform duration-700"></div>
              </Button>
            </nav>
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-white hover:text-emerald-400 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
        
        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-950 border-b border-emerald-500/20 shadow-2xl z-50">
            <nav className="container mx-auto px-6 py-6 flex flex-col gap-1">
              <Link 
                to="/services" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-gray-200 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all duration-300 font-semibold text-lg py-3 px-4 rounded-lg"
              >
                Services
              </Link>
              <Link 
                to="/commercial" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-gray-200 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all duration-300 font-semibold text-lg py-3 px-4 rounded-lg"
              >
                Commercial
              </Link>
              <Link 
                to="/gallery" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-gray-200 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all duration-300 font-semibold text-lg py-3 px-4 rounded-lg"
              >
                Gallery
              </Link>
              <Link 
                to="/values" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-gray-200 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all duration-300 font-semibold text-lg py-3 px-4 rounded-lg"
              >
                Our Values
              </Link>
              <Link 
                to="/testimonials" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-gray-200 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all duration-300 font-semibold text-lg py-3 px-4 rounded-lg"
              >
                Testimonials
              </Link>
              <Link 
                to="/faq" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-gray-200 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all duration-300 font-semibold text-lg py-3 px-4 rounded-lg"
              >
                FAQ
              </Link>
              <Button 
                onClick={() => { scrollToContact(); setMobileMenuOpen(false); }}
                className="mt-4 bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-600 text-white font-bold px-8 py-3 rounded-full shadow-lg"
              >
                Get a Quote
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* PREMIUM HERO - Cinematic with Complex Typography */}
      <section className="relative pt-24 min-h-screen flex items-center overflow-hidden">
        {/* Animated Background Image - Ken Burns Zoom Out Effect */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 animate-ken-burns">
            <img 
              src="https://images.unsplash.com/photo-1755140584836-9771b5f51575?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBob21lJTIwZXh0ZXJpb3IlMjBzdG9uZSUyMHdhbGt3YXklMjBsYW5kc2NhcGluZyUyMGV2ZW5pbmd8ZW58MHx8fHwxNzg4NDczOTkzfDA&ixlib=rb-4.1.0&q=85" 
              alt="Luxury Property with Premium Landscaping"
              className="w-full h-full object-cover scale-125"
            />
          </div>
          {/* Multi-layer gradient overlay for depth */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-emerald-950/40 to-slate-950/95"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_rgba(0,0,0,0.4)_100%)]"></div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-32 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-32 right-10 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-emerald-400 rounded-full animate-pulse-glow"></div>
        <div className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse-glow animation-delay-400"></div>
        <div className="absolute bottom-1/3 left-1/3 w-2.5 h-2.5 bg-emerald-300 rounded-full animate-pulse-glow animation-delay-800"></div>
        
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '50px 50px'}}></div>
        
        {/* Content */}
        <div className="container mx-auto relative z-10 px-4 lg:px-8">
          <div className="max-w-5xl mx-auto text-center">
            {/* Premium Badge */}
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-emerald-900/50 via-emerald-800/40 to-emerald-900/50 backdrop-blur-2xl border border-emerald-500/30 rounded-full mb-10 animate-fade-in-up shadow-[0_0_40px_rgba(16,185,129,0.2)]">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.8)]"></div>
              <span className="text-sm font-bold text-emerald-300 uppercase tracking-[0.2em]">Alberta's Trusted Property Services</span>
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.8)]"></div>
            </div>
            
            {/* Main Headline - Complex Typography */}
            <h1 className="mb-8 animate-fade-in-up animation-delay-200 px-6">
              <span className="block text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black tracking-tight leading-none">
                <span className="inline-block bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent drop-shadow-2xl" style={{textShadow: '0 0 80px rgba(255,255,255,0.3)'}}>
                  Transforming
                </span>
              </span>
              <span className="block text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black tracking-tight leading-none mt-2">
                <span className="inline-block bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent drop-shadow-2xl">
                  Alberta
                </span>
              </span>
              <span className="block mt-4">
                <span className="relative inline-block text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black tracking-tight leading-none">
                  <span className="bg-gradient-to-r from-emerald-400 via-green-300 to-emerald-400 bg-clip-text text-transparent animate-gradient-x" style={{textShadow: '0 0 60px rgba(16,185,129,0.5)'}}>
                    Landscapes
                  </span>
                  {/* Underline decoration */}
                  <svg className="absolute -bottom-4 left-0 w-full h-4" viewBox="0 0 200 12" preserveAspectRatio="none">
                    <path d="M0,6 Q50,0 100,6 T200,6" stroke="url(#gradient)" strokeWidth="3" fill="none" className="animate-draw-line"/>
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#10b981" stopOpacity="0"/>
                        <stop offset="50%" stopColor="#34d399" stopOpacity="1"/>
                        <stop offset="100%" stopColor="#10b981" stopOpacity="0"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </span>
              </span>
            </h1>
            
            {/* Tagline with elegant styling */}
            <div className="max-w-3xl mx-auto mb-12 animate-fade-in-up animation-delay-400">
              <p className="text-xl sm:text-2xl lg:text-2xl text-gray-200 leading-relaxed font-light">
                Professional landscaping and snow removal services for
                <span className="font-semibold text-white"> residential</span> and
                <span className="font-semibold text-white"> commercial </span>
                properties.
              </p>
              <p className="mt-4 text-xl sm:text-2xl text-emerald-300 font-semibold tracking-wide">
                Year-round excellence, family owned and locally operated.
              </p>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-5 justify-center items-center animate-fade-in-up animation-delay-600">
              <Button 
                onClick={scrollToContact} 
                size="lg" 
                className="group relative overflow-hidden bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-600 hover:from-emerald-500 hover:via-green-400 hover:to-emerald-500 text-white px-12 py-8 text-xl font-bold rounded-2xl shadow-[0_8px_40px_rgba(16,185,129,0.4)] hover:shadow-[0_12px_50px_rgba(16,185,129,0.6)] transition-all duration-500 border border-emerald-400/30"
                data-testid="hero-get-quote-btn"
              >
                <span className="relative z-10 flex items-center gap-3">
                  Get a Free Quote
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              </Button>
              <Link to="/gallery">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="relative overflow-hidden border-2 border-white/40 text-white hover:border-emerald-400/60 hover:text-emerald-300 backdrop-blur-sm px-12 py-8 text-xl font-bold rounded-2xl hover:shadow-[0_8px_40px_rgba(16,185,129,0.2)] transition-all duration-500 group"
                  data-testid="hero-view-work-btn"
                >
                  <span className="relative z-10">View Our Work</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/0 via-emerald-600/10 to-emerald-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </Button>
              </Link>
            </div>
            
            {/* Premium Stats Bar */}
            <div className="mt-20 animate-fade-in-up animation-delay-800">
              <div className="inline-flex flex-wrap justify-center items-center gap-4 sm:gap-8 px-8 py-6 bg-gradient-to-r from-slate-900/80 via-emerald-950/60 to-slate-900/80 backdrop-blur-2xl rounded-3xl border border-emerald-500/20 shadow-[0_8px_60px_rgba(0,0,0,0.4)]">
                {/* Stat 1 */}
                <div className="text-center px-6 py-2">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl sm:text-5xl font-black bg-gradient-to-b from-emerald-300 to-emerald-500 bg-clip-text text-transparent">500</span>
                    <span className="text-2xl sm:text-3xl font-black text-emerald-400">+</span>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-400 uppercase tracking-widest font-semibold mt-1">Projects Completed</div>
                </div>
                
                {/* Divider */}
                <div className="hidden sm:block w-px h-16 bg-gradient-to-b from-transparent via-emerald-500/50 to-transparent"></div>
                
                {/* Stat 2 */}
                <div className="text-center px-6 py-2">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl sm:text-5xl font-black bg-gradient-to-b from-emerald-300 to-emerald-500 bg-clip-text text-transparent">24</span>
                    <span className="text-2xl sm:text-3xl font-black text-emerald-400">/7</span>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-400 uppercase tracking-widest font-semibold mt-1">Snow Response</div>
                </div>
                
                {/* Divider */}
                <div className="hidden sm:block w-px h-16 bg-gradient-to-b from-transparent via-emerald-500/50 to-transparent"></div>
                
                {/* Stat 3 - 5 Star Rating with Google Reviews Link */}
                <a 
                  href="https://share.google/zlctzrPc6tgSHvchd" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-center px-6 py-2 group cursor-pointer hover:scale-105 transition-transform"
                >
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl sm:text-5xl font-black bg-gradient-to-b from-amber-300 to-amber-500 bg-clip-text text-transparent">5</span>
                    <Star className="w-7 h-7 sm:w-8 sm:h-8 text-amber-400 fill-amber-400" />
                  </div>
                  <div className="text-xs sm:text-sm text-gray-400 uppercase tracking-widest font-semibold mt-1">Client Rating</div>
                  <div className="flex items-center justify-center gap-1 mt-2 text-amber-400 group-hover:text-amber-300 transition-colors">
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className="text-xs font-bold">View Reviews</span>
                  </div>
                </a>
                
                {/* Divider */}
                <div className="hidden sm:block w-px h-16 bg-gradient-to-b from-transparent via-emerald-500/50 to-transparent"></div>
                
                {/* Stat 4 - Location */}
                <div className="text-center px-6 py-2">
                  <div className="flex items-center justify-center gap-2">
                    <MapPin className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-400" />
                    <span className="text-lg sm:text-xl font-bold text-white">Calgary</span>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-400 uppercase tracking-widest font-semibold mt-1">& Surrounding Areas</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Showcase Section - Our Expertise */}
      <section className="relative py-32 px-4 lg:px-8 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent"></div>
        <div className="absolute top-20 right-20 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-green-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto relative z-10">
          {/* Section Header */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
              <span className="text-emerald-400 text-sm font-semibold uppercase tracking-widest">What We Do</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6">
              Our <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Expertise</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Specialized in creating and maintaining exceptional outdoor environments for discerning clients
            </p>
          </div>
          
          {/* Cards Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Card 1 - Residential */}
            <div className="group relative overflow-hidden rounded-3xl aspect-[4/5] cursor-pointer">
              <img 
                src="https://images.unsplash.com/photo-1661333587578-8ef88d297b1f" 
                alt="Residential Properties"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent group-hover:via-emerald-950/40 transition-all duration-500"></div>
              {/* Glowing border on hover */}
              <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-emerald-500/50 transition-all duration-500 shadow-[inset_0_0_30px_rgba(16,185,129,0)] group-hover:shadow-[inset_0_0_30px_rgba(16,185,129,0.1)]"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-1 bg-gradient-to-r from-emerald-400 to-green-400 rounded-full group-hover:w-12 transition-all duration-300"></div>
                </div>
                <h3 className="text-3xl font-black text-white mb-2 group-hover:text-emerald-300 transition-colors duration-300">Residential</h3>
                <p className="text-gray-300 text-lg">Homes to private estates and luxury residences</p>
              </div>
            </div>
            
            {/* Card 2 - Commercial */}
            <div className="group relative overflow-hidden rounded-3xl aspect-[4/5] cursor-pointer">
              <img 
                src="https://images.unsplash.com/photo-1760140410958-6168e226b376?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMGNhbmFkYSUyMGxhbmRzY2FwaW5nfGVufDB8fHx8MTc3MjQ5ODU2Nnww&ixlib=rb-4.1.0&q=85" 
                alt="Commercial Properties"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent group-hover:via-emerald-950/40 transition-all duration-500"></div>
              <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-emerald-500/50 transition-all duration-500 shadow-[inset_0_0_30px_rgba(16,185,129,0)] group-hover:shadow-[inset_0_0_30px_rgba(16,185,129,0.1)]"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-1 bg-gradient-to-r from-emerald-400 to-green-400 rounded-full group-hover:w-12 transition-all duration-300"></div>
                </div>
                <h3 className="text-3xl font-black text-white mb-2 group-hover:text-emerald-300 transition-colors duration-300">Commercial</h3>
                <p className="text-gray-300 text-lg">Office complexes and retail spaces</p>
              </div>
            </div>
            
            {/* Card 3 - Communities */}
            <div className="group relative overflow-hidden rounded-3xl aspect-[4/5] cursor-pointer">
              <img 
                src="https://images.unsplash.com/photo-1723438095835-220f5a82bed4" 
                alt="Communities"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent group-hover:via-emerald-950/40 transition-all duration-500"></div>
              <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-emerald-500/50 transition-all duration-500 shadow-[inset_0_0_30px_rgba(16,185,129,0)] group-hover:shadow-[inset_0_0_30px_rgba(16,185,129,0.1)]"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-1 bg-gradient-to-r from-emerald-400 to-green-400 rounded-full group-hover:w-12 transition-all duration-300"></div>
                </div>
                <h3 className="text-3xl font-black text-white mb-2 group-hover:text-emerald-300 transition-colors duration-300">Communities</h3>
                <p className="text-gray-300 text-lg">HOAs, condos, and gated communities</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Services Section - With Moving Snow Clearing Background */}
      <section id="services" className="relative py-32 px-4 lg:px-8 overflow-hidden">
        {/* Animated Background - Skid Steer */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 animate-ken-burns">
            <img 
              src="https://images.unsplash.com/photo-1763515187478-9934edce7158?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxsb2FkZXIlMjB0cmFjdG9yJTIwc25vdyUyMHJlbW92YWwlMjBkcml2ZXdheSUyMGNsZWFyaW5nJTIwd2ludGVyfGVufDB8fHx8MTc3MjM4OTE2NXww&ixlib=rb-4.1.0&q=85" 
              alt="Skid Steer Snow Removal"
              className="w-full h-full object-cover scale-125"
            />
          </div>
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-900/85 to-slate-950/95"></div>
        </div>
        
        <div className="container mx-auto relative z-10">
          {/* Section Header */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 backdrop-blur-sm border border-emerald-500/30 rounded-full mb-6">
              <span className="text-emerald-300 text-sm font-semibold uppercase tracking-widest">Our Services</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6">
              Premium <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Services</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Comprehensive property care designed for Alberta's unique climate
            </p>
          </div>
          
          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="group relative overflow-hidden bg-slate-900/60 backdrop-blur-xl hover:bg-slate-800/80 border-2 border-slate-700/50 hover:border-emerald-500/50 rounded-3xl transition-all duration-500 hover:shadow-[0_20px_60px_rgba(16,185,129,0.2)]">
                  {/* Top accent line */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-green-400 to-emerald-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                  
                  {/* ASM Badge for Snow Removal */}
                  {service.badge && (
                    <div className="absolute top-4 right-4 bg-white rounded-lg p-2 shadow-lg">
                      <img 
                        src="https://customer-assets-lqy194kg.emergentagent.net/job_90e246be-0711-4e27-8caa-13f1ea345ba5/artifacts/1valnauv_ASM%20Logo.png" 
                        alt="SIMA ASM Certified" 
                        className="h-8 w-auto"
                      />
                    </div>
                  )}
                  
                  <CardHeader className="pt-8">
                    {/* Icon with glow */}
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-emerald-400/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      <div className="relative w-16 h-16 bg-gradient-to-br from-emerald-600 via-green-500 to-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg group-hover:shadow-emerald-500/30">
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <CardTitle className="text-2xl font-black text-white mb-3 group-hover:text-emerald-400 transition-colors duration-300">{service.title}</CardTitle>
                    <CardDescription className="text-gray-400 text-base leading-relaxed">{service.description}</CardDescription>
                  </CardHeader>
                  
                  <CardContent className="pb-8">
                    <ul className="space-y-3 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-gray-300">
                          <div className="w-5 h-5 mt-0.5 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={service.customPage || `/services/${service.title.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}>
                      <Button className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl transition-all duration-300 group-hover:shadow-lg">
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Before/After Gallery Section - Premium Dark */}
      <section id="gallery" className="py-24 px-4 lg:px-8 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
              <span className="text-emerald-400 text-sm font-semibold uppercase tracking-widest">Portfolio</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-4">Our <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Work</span></h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              See the transformations we've created for Alberta homeowners and businesses.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {beforeAfterImages.map((project) => (
              <Card key={project.id} className="group overflow-hidden bg-slate-800/50 backdrop-blur-sm border-2 border-slate-700/50 hover:border-emerald-500/50 rounded-3xl transition-all duration-500 hover:shadow-[0_20px_60px_rgba(16,185,129,0.15)]">
                <div className="relative bg-slate-900">
                  <img 
                    src={project.before} 
                    alt={project.title}
                    className="w-full h-96 object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-gradient-to-r from-emerald-600 to-green-600 px-4 py-2 rounded-full">
                    <span className="text-sm font-bold text-white">Before & After</span>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl text-white group-hover:text-emerald-400 transition-colors">{project.title}</CardTitle>
                  <CardDescription className="text-gray-400">{project.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Company Values Section - Premium Dark */}
      <section id="values" className="py-24 px-4 lg:px-8 bg-slate-950">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
              <span className="text-emerald-400 text-sm font-semibold uppercase tracking-widest">Who We Are</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-4">Our Core <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Values</span></h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              The principles that guide every project we undertake and every relationship we build.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {companyValues.map((value) => {
              const IconComponent = iconMap[value.icon];
              return (
                <div key={value.id} className="group">
                  <Card className="h-full bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-2 border-slate-700/50 hover:border-emerald-500/50 rounded-3xl transition-all duration-500 hover:shadow-[0_20px_60px_rgba(16,185,129,0.1)]">
                    <CardHeader>
                      <div className="w-14 h-14 bg-gradient-to-br from-emerald-600 to-green-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <CardTitle className="text-2xl text-white group-hover:text-emerald-400 transition-colors">{value.title}</CardTitle>
                      <CardDescription className="text-gray-400 text-base leading-relaxed">{value.description}</CardDescription>
                    </CardHeader>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Premium Dark */}
      <section id="testimonials" className="py-24 px-4 lg:px-8 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full mb-6">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span className="text-amber-400 text-sm font-semibold uppercase tracking-widest">Reviews</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-4">What Our <span className="bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent">Clients Say</span></h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Don't just take our word for it — hear from satisfied property owners across Alberta.
            </p>
            {/* Google Reviews Button */}
            <a 
              href="https://share.google/zlctzrPc6tgSHvchd" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white hover:bg-gray-100 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="font-bold text-gray-800 group-hover:text-gray-900">View Our Google Reviews</span>
              <ArrowRight className="w-5 h-5 text-gray-600 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={testimonial.id} className="group bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-2 border-slate-700/50 hover:border-amber-500/30 rounded-3xl transition-all duration-500 hover:shadow-[0_20px_60px_rgba(245,158,11,0.1)]">
                <CardHeader>
                  <div className="flex gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <CardDescription className="text-gray-300 text-base leading-relaxed italic">
                    "{testimonial.text}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border-t border-slate-700/50 pt-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                      index % 3 === 0 ? 'bg-gradient-to-br from-emerald-600 to-green-700' :
                      index % 3 === 1 ? 'bg-gradient-to-br from-amber-600 to-orange-700' :
                      'bg-gradient-to-br from-blue-600 to-cyan-700'
                    }`}>
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-white">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {/* Bottom Google Reviews CTA */}
          <div className="mt-12 text-center">
            <a 
              href="https://share.google/zlctzrPc6tgSHvchd" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-semibold transition-colors"
            >
              <Star className="w-5 h-5 fill-amber-400" />
              See all our 5-star reviews on Google
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ CTA Section */}
      <section className="py-16 px-4 lg:px-8 bg-slate-950">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-2 border-slate-700/50 rounded-3xl p-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
              <span className="text-emerald-400 text-sm font-semibold uppercase tracking-widest">FAQ</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">Have <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Questions?</span></h2>
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
              Find answers to common questions about our landscaping services, pricing, and processes.
            </p>
            <Link to="/faq">
              <Button className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-bold px-10 py-6 text-lg rounded-2xl shadow-lg">
                View All FAQs
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section - Premium Dark */}
      <section id="contact" className="py-24 px-4 lg:px-8 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
              <span className="text-emerald-400 text-sm font-semibold uppercase tracking-widest">Contact Us</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-4">Get In <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Touch</span></h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Ready to transform your property? Complete the quote request form below and our team will review your project details and follow up with next steps.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <Card className="bg-slate-800/50 backdrop-blur-sm border-2 border-slate-700/50 rounded-3xl">
                <CardHeader>
                  <CardTitle className="text-2xl text-white">Request a Quote</CardTitle>
                  <CardDescription className="text-gray-400">Please complete the form below. Our team will review your request and contact you regarding next steps.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-300">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Your full name"
                        className="bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-emerald-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-300">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        placeholder="(403) 123-4567"
                        className="bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-emerald-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-300">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your.email@example.com"
                        className="bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-emerald-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service" className="text-gray-300">Service Interested In *</Label>
                      <Input
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Landscape Design, Snow Removal"
                        className="bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-emerald-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address" className="text-gray-300">Service Address *</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                        placeholder="Your property address"
                        className="bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-emerald-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="timeframe" className="text-gray-300">Preferred Start Timeframe</Label>
                      <Input
                        id="timeframe"
                        name="timeframe"
                        value={formData.timeframe}
                        onChange={handleInputChange}
                        placeholder="e.g., ASAP, Within 2 weeks, Spring 2025"
                        className="bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-emerald-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-gray-300">Project Details *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        placeholder="Tell us about your project..."
                        rows={5}
                        className="bg-slate-900/50 border-slate-600 text-white placeholder:text-gray-500 focus:border-emerald-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Upload Project Photos</Label>
                      <div className="relative">
                        <input
                          type="file"
                          id="photos"
                          multiple
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="photos"
                          className="flex items-center justify-center gap-2 w-full py-4 px-4 bg-slate-900/50 border-2 border-dashed border-slate-600 rounded-xl text-gray-400 hover:border-emerald-500 hover:text-emerald-400 cursor-pointer transition-all"
                        >
                          <Upload className="w-5 h-5" />
                          <span>Upload Project Photos</span>
                        </label>
                      </div>
                      {photos.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {photos.map((photo, index) => (
                            <div key={index} className="flex items-center gap-2 bg-slate-700/50 px-3 py-1 rounded-full text-sm text-gray-300">
                              <span className="truncate max-w-[150px]">{photo.name}</span>
                              <button
                                type="button"
                                onClick={() => removePhoto(index)}
                                className="text-gray-400 hover:text-red-400"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button type="submit" className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-bold py-6 text-lg rounded-xl">
                      Submit Quote Request
                    </Button>
                    {submitStatus && (
                      <p className="text-center text-emerald-400 font-medium">{submitStatus}</p>
                    )}
                  </form>
                </CardContent>
              </Card>
            </div>
            <div className="space-y-8">
              <Card className="bg-slate-800/50 backdrop-blur-sm border-2 border-slate-700/50 rounded-3xl">
                <CardHeader>
                  <CardTitle className="text-2xl text-white">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-white mb-1">Phone</p>
                      <a href="tel:5873252799" className="text-gray-400 hover:text-emerald-400 transition-colors">
                        587-325-2799
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-white mb-1">Email</p>
                      <a href="mailto:info@truenorthalberta.ca" className="text-gray-400 hover:text-emerald-400 transition-colors">
                        info@truenorthalberta.ca
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-white mb-1">Service Area</p>
                      <p className="text-gray-400">Calgary and surrounding areas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-emerald-600 to-green-600 border-0 rounded-3xl">
                <CardHeader>
                  <CardTitle className="text-2xl text-white">Year-Round Service</CardTitle>
                  <CardDescription className="text-emerald-100">
                    From spring landscaping to winter snow removal, we're here for you every season.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                      <Leaf className="w-8 h-8 mx-auto mb-2 text-white" />
                      <p className="font-bold text-white">Spring-Fall</p>
                      <p className="text-sm text-emerald-100">Landscaping</p>
                    </div>
                    <div className="text-center p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                      <Snowflake className="w-8 h-8 mx-auto mb-2 text-white" />
                      <p className="font-bold text-white">Winter</p>
                      <p className="text-sm text-emerald-100">Snow Removal</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Footer */}
      <footer className="relative bg-gradient-to-b from-slate-900 via-slate-950 to-black text-gray-300 pt-20 pb-8 px-4 lg:px-8 overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent"></div>
        <div className="absolute top-20 left-10 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-green-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto relative z-10">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            {/* Logo & Description */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-4 mb-6">
                <div className="relative">
                  <div className="absolute -inset-2 bg-emerald-500/20 rounded-xl blur-xl"></div>
                  <img 
                    src="https://customer-assets-lqy194kg.emergentagent.net/job_90e246be-0711-4e27-8caa-13f1ea345ba5/artifacts/pl6ilas6_TNA%20Logo.png" 
                    alt="True North Alberta Property Services" 
                    className="relative h-20 w-auto rounded-xl shadow-lg"
                  />
                </div>
                <div>
                  <span className="text-xl font-black text-white">TRUE NORTH</span>
                  <span className="block text-xs text-emerald-400 tracking-[0.2em]">ALBERTA PROPERTY SERVICES</span>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed max-w-md">
                Professional landscaping and snow removal services for Alberta properties. Quality work, reliable service, year-round excellence.
              </p>
              <div className="flex items-center gap-2 mt-6">
                <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full border border-emerald-500/20">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                  <span className="text-sm text-emerald-400 font-medium">Locally Owned & Insured</span>
                </div>
              </div>
            </div>
            
            {/* Quick Links */}
            <div>
              <h3 className="font-bold text-white mb-6 text-lg">Quick Links</h3>
              <ul className="space-y-3">
                <li><Link to="/services" className="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group"><ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />Services</Link></li>
                <li><Link to="/gallery" className="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group"><ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />Gallery</Link></li>
                <li><Link to="/values" className="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group"><ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />Our Values</Link></li>
                <li><Link to="/testimonials" className="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group"><ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />Testimonials</Link></li>
                <li><a href="#contact" className="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group"><ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />Contact</a></li>
              </ul>
            </div>
            
            {/* Contact & Service Area */}
            <div>
              <h3 className="font-bold text-white mb-6 text-lg">Service Area</h3>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white font-semibold">Calgary</p>
                  <p className="text-gray-500 text-sm">& Surrounding Areas</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Call Us</p>
                  <a href="tel:5873252799" className="text-white font-semibold hover:text-emerald-400 transition-colors">587-325-2799</a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} True North Alberta Property Services. All rights reserved.</p>
              <div className="flex items-center gap-6">
                <span className="text-gray-600 text-sm">Year-Round Excellence</span>
                <div className="flex items-center gap-2">
                  <Leaf className="w-4 h-4 text-emerald-500" />
                  <Snowflake className="w-4 h-4 text-blue-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;